import {ComboboxInput, ComboboxItem, ComboboxPopover} from '@/components/ui/combobox-atoms/ComboboxAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useDiseases} from '@/features/treatment-requests/services/useDiseases'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, useComboboxStore} from '@ariakit/react'
import {DiseaseBase, DiseaseExtended, DiseaseSmall} from '@/features/treatment-requests/types'
import {useInputSearch} from '@/hooks/useInputSearch'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {SelectComboboxList, SelectVirtualList} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {SearchSmIcon} from '@/components/ui/icon/Icon'
import {StyledSelectListItemDisease} from './style'
import {useAsync} from '@/hooks/useAsync'
import {httpGetDisease} from '@/features/treatment-requests/services/treatment-requests.http'
import {PatientAgeRange} from '@/types'
import {Badge} from '@/components/ui/badge/Badge'
import {diseasesGroupNameToLabel} from '@/features/treatment-requests/utils'
import {useRef} from 'react'

interface InputSearchDiseaseProps {
    errorMessage?: string
    placeholder?: string
    defaultValue?: DiseaseExtended
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
    onChange: (value: DiseaseExtended) => void
    onClear?: () => void
}

export const InputSearchDisease = ({
    errorMessage,
    placeholder,
    serviceType,
    defaultValue,
    onChange,
    onClear
}: InputSearchDiseaseProps) => {
    const {t} = useTranslation()
    const {searchValue, onSearch, onResetSearch} = useInputSearch()
    const comboboxInputRef = useRef(null)

    const diseasesQuery = useDiseases(
        {
            serviceType,
            search: searchValue,
            limit: 15,
            responseType: 'small'
        },
        {enabled: !!serviceType && !!searchValue}
    )

    const selectedGroupDetailsQuery = useAsync()

    const comboboxStore = useComboboxStore({
        defaultValue: defaultValue?.name,
        setValue: onSearch
    })

    const handleSelect = async (item: DiseaseBase) => {
        onResetSearch(comboboxInputRef)
        try {
            const {data} = await selectedGroupDetailsQuery.run(
                httpGetDisease({
                    urlParams: {diseaseId: item.id},
                    params: {responseType: 'extended'}
                })
            )

            onChange(data)
        } catch (error) {
            console.error(error)
        }

        comboboxStore.setOpen(false)
    }

    const handleClear = () => {
        onSearch('')
        onClear?.()
    }

    return (
        <ComboboxProvider store={comboboxStore} resetValueOnHide>
            <Flexbox direction="column" gap={1.5} fullWidth>
                <ComboboxInput
                    hasCloseCombobox
                    typeIcon={<SearchSmIcon size={16} />}
                    errorMessage={errorMessage}
                    placeholder={placeholder || t('commons:search_placeholder')}
                    onClear={handleClear}
                    ref={comboboxInputRef}
                />
                {!!errorMessage && (
                    <Flexbox align="center" gap={1.5}>
                        <InputStatusIcon hasError={true} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                )}
            </Flexbox>
            <ComboboxPopover hidden={!searchValue}>
                <SelectComboboxList>
                    {diseasesQuery.isLoading ? (
                        <Flexbox direction="column" gap={2} fullWidth>
                            {Array.from({length: 6}, (_, index) => (
                                <Flexbox key={index} gap={2} fullWidth>
                                    <Skeleton width={20} height={20} />
                                    <Skeleton width="100%" height={32} />
                                </Flexbox>
                            ))}
                        </Flexbox>
                    ) : diseasesQuery.isError ? (
                        <ComboboxItem disabled>
                            <InputHelpText error={t('errors:default')} />
                        </ComboboxItem>
                    ) : diseasesQuery.remappedData.length > 0 ? (
                        <SelectComboboxList>
                            <SelectVirtualList
                                data={diseasesQuery.remappedData}
                                totalCount={diseasesQuery.remappedData.length}
                                overscan={100}
                                endReached={() => diseasesQuery.hasNextPage && diseasesQuery.fetchNextPage()}
                                itemContent={(itemIndex, item: DiseaseSmall) => {
                                    const labelKey = item.rootParent.name as keyof typeof diseasesGroupNameToLabel
                                    return (
                                        <StyledSelectListItemDisease key={itemIndex} onClick={() => handleSelect(item)}>
                                            <Flexbox align="flex-start" justify="space-between" fullWidth gap={1}>
                                                {item.name}
                                                <Badge style={{whiteSpace: 'nowrap'}} size="sm" shape="rectangle">
                                                    {t(diseasesGroupNameToLabel[labelKey])}
                                                </Badge>
                                            </Flexbox>
                                        </StyledSelectListItemDisease>
                                    )
                                }}
                            />
                        </SelectComboboxList>
                    ) : (
                        <InputHelpText helpText={t('commons:no_results')} />
                    )}
                </SelectComboboxList>
            </ComboboxPopover>
        </ComboboxProvider>
    )
}
